import { IGenericModal, Modal } from 'components/Modal/Modal';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  buildPriorityLevelPayload,
  emptyPriorityLevelTranslations,
  PriorityLevelForm
} from '../PriorityLevelForm/PriorityLevelForm';
import { IPriorityLevel, IPriorityLevelFormFields } from 'types/priorityLevel';
import { createPriorityLevel } from 'api/priorityLevels';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { priorityLevelSchema } from 'validation/priorityLevelSchemas';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useMemo } from 'react';
import { expandSelectedAreaIdsWithAncestors } from 'components/_form/NestedSelect/utilities';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';

interface Props extends IGenericModal {
  onSuccess?: (priorityLevel: IPriorityLevel) => void;
  priorityLevelsCount: number;
}

export const AddPriorityLevelForm = ({
  isOpen,
  onCancelClick,
  onSuccess,
  priorityLevelsCount
}: Props) => {
  const { t } = useTranslation();
  const { data: mainCompany } = useMainCompanyQuery();

  const methods = useForm<IPriorityLevelFormFields & { active?: boolean }>({
    mode: 'onChange',
    defaultValues: {
      active: true,
      color_code: '#cccccc',
      ...emptyPriorityLevelTranslations(mainCompany)
    },
    resolver: yupResolver(priorityLevelSchema)
  });

  const { data: areasData = [] } = useAreasQuery();
  const areaTreeRoots = useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );

  const { handleSubmit } = methods;

  const handleSubmitPriorityLevelForm = async (
    data: IPriorityLevelFormFields & { active?: boolean }
  ) => {
    const areaIdsForApi = expandSelectedAreaIdsWithAncestors(
      areaTreeRoots,
      data.areas ?? []
    );

    const priorityLevelRes = await createPriorityLevel({
      priority_level: {
        ...buildPriorityLevelPayload(data, areaIdsForApi, mainCompany),
        order: priorityLevelsCount + 1
      }
    });

    if ('error' in priorityLevelRes) {
      toast.error(priorityLevelRes.error.message);
      return;
    }

    onSuccess?.(priorityLevelRes.data);
  };

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={() => {
        onCancelClick();
      }}
      headerTitle={t('administrationTasksView.addPriority')}
      mainButton={{
        action: handleSubmit((data) => {
          handleSubmitPriorityLevelForm(data);
        }),
        variant: 'eucalyptus',
        label: t('buttons.save')
      }}
      isSecondButtonVisible
      gridTemplateColumns="70%"
    >
      <FormProvider {...methods}>
        <form>
          <PriorityLevelForm />
        </form>
      </FormProvider>
    </Modal>
  );
};
